import { Box, Heading, Text, VStack } from "@chakra-ui/react";

import { FacilitySearchCombobox } from "#app/components/FacilitySearchCombobox.jsx";
import { ReviewProof } from "./ReviewProof.jsx";
import { GlowFrame } from "./GlowFrame.jsx";

/**
 * Search-first hero. The search box (inside the brand GlowFrame) is the primary
 * action, with the review social-proof bar directly beneath it. The explainer
 * video and the real-alerts proof feed follow further down the page.
 *
 * Used by the homepage and the country pages (/au, /nz). The global home hero
 * leads with the country pills (`showCountryPills`); country pages instead pass
 * a `country` (which scopes search copy + results and surfaces the per-country
 * "what we cover" helper line under the search). Featured facility *groups*
 * pass `facilityIds` to scope the search. Single-facility pages use
 * FacilityHero instead (no search).
 */
export function HomeHero({
  heading,
  subtitle,
  reviews = [],
  stats,
  agencyIds,
  facilityIds,
  country,
  showCountryPills = false,
}) {
  return (
    <VStack
      spacing={0}
      textAlign="center"
      px={{ base: 4, md: 6 }}
      pt={{ base: 4, md: 10 }}
      maxW="container.md"
      mx="auto"
      w="100%"
    >
      <Heading
        as="h1"
        fontSize={{ base: "4xl", md: "5xl" }}
        lineHeight="1.05"
        letterSpacing="tight"
        fontWeight="extrabold"
      >
        {heading} fully&nbsp;booked?{" "}
        <Text as="span" color="warning">
          Schnerp&nbsp;it!
        </Text>
      </Heading>

      <Text mt={4} fontSize={{ base: "lg", md: "xl" }} maxW="xl" color="muted">
        {subtitle}
      </Text>

      {/* Search defaults (title, placeholder, helper copy) are shared with the
          /new request page. `align="center"` is hero-only. The helper line shows
          on country pages (where `country` scopes it to a concise "what we cover"
          sentence) and stays hidden on the global home hero, which leads with
          the country pills instead. */}
      <GlowFrame
        mt={8}
        w="100%"
        innerProps={{
          px: { base: 5, md: 10 },
          py: { base: 7, md: 9 },
          textAlign: "center",
        }}
      >
        <FacilitySearchCombobox
          align="center"
          showHelper={!!country}
          country={country}
          agencyIds={agencyIds}
          facilityIds={facilityIds}
          showCountryPills={showCountryPills}
          pillsPlacement="bottom"
        />
      </GlowFrame>

      <Box mt={6}>
        <ReviewProof reviews={reviews} stats={stats} />
      </Box>
    </VStack>
  );
}
